// useLogin.js
// Custom hook for managing Login state and authentication logic

import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, signOut, sendEmailVerification } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../services/firebase';
import { formatAuthError, formatFirestoreError } from '../../utils/errorMessages';

// Helper function for retrying Firestore operations
async function safeGetDoc(ref, retries = 2) {
  for (let i = 0; i <= retries; i++) {
    try {
      return await getDoc(ref);
    } catch (e) {
      const transient = (e.code === 'unavailable') || (e.message || '').includes('offline');
      if (!transient || i === retries) throw e;
      await new Promise(r => setTimeout(r, 400 * (i + 1)));
    }
  }
}

const useLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [unverifiedUser, setUnverifiedUser] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [cooldown, setCooldown] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const lastErrorRef = useRef(0);
  const navigate = useNavigate();

  // Validate ISU email
  const validateEmail = (email) => {
    if (!email.endsWith('@ilstu.edu')) {
      setErrorMsg('Please use a valid ISU email address (@ilstu.edu).');
      return false;
    }
    return true;
  };

  // Handle user profile creation/update
  const handleUserProfile = async (user) => {
    const userRef = doc(db, 'users', user.uid);
    let userSnap = null;

    if (navigator.onLine !== false) {
      try {
        userSnap = await safeGetDoc(userRef);
      } catch (e) {
        // Profile fetch failed, continue without profile doc
      }
    }

    if (!userSnap || !userSnap.exists()) {
      const userData = {
        name: user.displayName || user.email.split('@')[0],
        email: user.email,
        joinedAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        profilePic: user.photoURL || ''
      };
      await setDoc(userRef, userData);
    } else {
      await updateDoc(userRef, {
        lastLogin: serverTimestamp()
      });
    }
  };

  // Format error messages using centralized utility
  const formatErrorMessage = (error) => {
    // Check if it's a Firestore error (permission-denied, etc.)
    if (error?.code?.startsWith('permission-denied') || error?.code === 'unavailable') {
      return formatFirestoreError(error);
    }
    // Otherwise treat as auth error
    return formatAuthError(error);
  };

  // Handle login process
  const handleLogin = async () => {
    if (loggingIn) return; // Prevent double submission

    try {
      setLoggingIn(true);
      setErrorMsg('');
      setUnverifiedUser(null); // Clear previous unverified user state

      if (!validateEmail(email)) {
        setLoggingIn(false);
        return;
      }

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // CRITICAL: Reload user to get latest emailVerified status from server
      await user.reload();
      
      // Check email verification with fresh data - MUST sign out BEFORE any state updates
      if (!user.emailVerified) {
        // Sign out IMMEDIATELY before any state changes
        await signOut(auth);
        
        // Now safe to update UI state
        setUnverifiedUser(user);
        setErrorMsg('Please verify your email before logging in.');
        setLoggingIn(false);
        return;
      }

      // Handle user profile
      await handleUserProfile(user);

      // Check if user was redirected here from seller-success page
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath);
        return;
      }

      // Fallback navigation if route guards don't redirect
      setTimeout(() => {
        if (window.location.pathname === '/login') {
          navigate('/market');
        }
      }, 2000);

    } catch (error) {
      const now = Date.now();
      const formattedMessage = formatErrorMessage(error);

      // Prevent error message spam
      if (now - lastErrorRef.current > 3000) {
        setErrorMsg(formattedMessage);
        lastErrorRef.current = now;
      }
      
      setLoggingIn(false);
    }
  };

  // Resend email verification
  const resendVerification = async () => {
    if (!unverifiedUser || cooldown) return;

    try {
      await sendEmailVerification(unverifiedUser);
      alert('Verification email sent successfully. Please check your inbox and Junk/Spam folder - verification emails often end up there!');
      setCooldown(true);
      setTimeout(() => setCooldown(false), 60000);
    } catch (error) {
      alert(formatAuthError(error));
    }
  };

  // Navigate to signup
  const navigateToSignup = () => {
    navigate('/signup');
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    unverifiedUser,
    errorMsg,
    cooldown,
    loggingIn,
    handleLogin,
    resendVerification,
    navigateToSignup
  };
};

export default useLogin;